import type { SubscribeToExtendedPage_extendedPage as Page } from "./__generated__/SubscribeToExtendedPage";

type Anchor = { utf16Start: number; utf16Size: number };

/** Build only the loaded page's UTF-16 positions; never allocate by book offset. */
export function indexCharacterMetadata(page: Page) {
  const pageStart = page.page.anchors[0].utf16Start;
  let start = pageStart;
  let end = pageStart + page.textContent.length;
  for (const rhese of page.rhese) {
    const anchor = rhese.anchors[0];
    const offset = anchor.utf16Start - pageStart;
    const renderedLength = page.textContent.substring(offset, offset + anchor.utf16Size).length;
    if (renderedLength) {
      start = Math.min(start, anchor.utf16Start);
      end = Math.max(end, anchor.utf16Start + renderedLength);
    }
  }
  const length = end - start;
  const positions = (anchors: readonly Anchor[], visit: (position: number) => void) => {
    for (const anchor of anchors) {
      const end = Math.min(length, anchor.utf16Start + anchor.utf16Size - start);
      for (let i = Math.max(0, anchor.utf16Start - start); i < end; i++) visit(i);
    }
  };
  const flag = (anchors: readonly Anchor[] = []) => {
    const result = new Uint8Array(length);
    positions(anchors, i => { result[i] = 1; });
    return result;
  };
  const kinds: (string[] | undefined)[] = new Array(length);
  for (const [items, kind] of [
    [page.textCitation, "CITATION"], [page.textWeight, "GRAS"], [page.textItalicization, "ITALIQUE"],
  ] as const) positions(items[0]?.anchors || [], i => {
    // Overlapping anchors within the same item still contribute one flag.
    const flags = kinds[i] ||= [];
    if (!flags.includes(kind)) flags.push(kind);
  });
  for (const [items, values, field] of [
    [page.textScriptNotation, { SUBSCRIPT: "INDICE", SUPERSCRIPT: "EXPOSANT" }, "textScriptNotation"],
    [page.textCase, { UPPER: "MAJUSCULE", LOWER: "MINUSCULE" }, "textCase"],
  ] as const) {
    for (const item of items) {
      // Preserve the converter's existing stop-on-empty and item-order semantics.
      if (!item.anchors.length) break;
      const value = (item.data as unknown as Record<string, string>)[field];
      const kind = (values as Record<string, string>)[value];
      if (!kind) continue;
      const covered = new Set<number>();
      positions(item.anchors, i => covered.add(i));
      for (const i of covered) (kinds[i] ||= []).push(kind);
    }
  }
  const first = <T>(items: readonly { anchors: readonly Anchor[]; data: T }[], value: (data: T) => string | null) => {
    const result: (string | null | undefined)[] = new Array(length);
    for (const item of items) {
      // These metadata families historically use only their first anchor.
      if (item.anchors[0]) positions([item.anchors[0]], i => {
        if (result[i] === undefined) result[i] = value(item.data);
      });
    }
    return result;
  };
  const phoneme: (string | undefined)[] = new Array(length);
  const phonemeKinds: Record<string, string> = { VOWEL: "vowel", CONSONANT: "consonant", COMPLEX: "complicated" };
  for (const item of page.phoneme) {
    const kind = phonemeKinds[item.data.kind];
    if (kind) positions(item.anchors, i => { phoneme[i] = kind; }); // Last matching item wins.
  }
  return {
    start, kinds, phoneme, muted: flag(page.textMuted[0]?.anchors),
    syllable: first(page.syllable, data => data.id),
    coloration: first(page.customColoration, data => data.customColorationKey),
    css: first(page.customCss, data => data.customCssClasses),
  };
}
